/**
 * useReportSubmit — offline-durable submit + flush hook for report-problems
 * (T35.6.3).
 *
 * Submit path (durable-first):
 *   1. write the report to the on-device outbox (survives an app restart);
 *   2. try to send it now; on the server's confirmation, clear it from the
 *      outbox. Offline / a failed send leaves it queued — never lost.
 *
 * Screenshots are NOT queued offline (binary blobs only sent while online, per
 * task spec). The submit call accepts an optional screenshots map that is
 * forwarded to the server but is not persisted to the outbox.
 *
 * Flush path: `flush()` drains the queue (oldest first). A permanently-rejected
 * item (4xx that isn't 401) is dropped so the queue can't wedge. A 401 /
 * network error is left for the next flush. Flushes on mount + on reconnect.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  postReportProblem,
  type ReportProblemSubmission,
  type ReportItemContext,
  type ReportProblemSeverity,
} from './api';
import {
  enqueueReportProblem,
  listQueuedReportProblems,
  removeQueuedReportProblem,
  type QueuedReportProblem,
} from './outbox';
import { randomUuid } from '../../lib/randomUuid';

/** Outcome of a submit: sent now, or queued for a later flush. */
export type ReportSubmitOutcome = 'sent' | 'queued';

export interface SubmitReportProblemArgs {
  note?: string | null;
  severity?: ReportProblemSeverity | null;
  screenContext?: string | null;
  itemContext?: ReportItemContext | null;
  env?: { appBuild: string; device: string; online: boolean } | null;
  screenshots?: Record<string, unknown> | null;
  isConsentToContact?: boolean;
}

export interface ReportSubmit {
  /** Persist + try to send one report. Resolves with where it landed. */
  submit: (args: SubmitReportProblemArgs) => Promise<ReportSubmitOutcome>;
  /** Drain the durable queue (oldest first). Safe to call repeatedly. */
  flush: () => Promise<void>;
}

/** True when the browser reports it is online (assume online when unknown). */
function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

/** A 4xx other than 401 is a permanent reject (bad payload) — drop, don't loop. */
function isPermanentReject(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 401;
}

function toSubmission(
  report: QueuedReportProblem,
  screenshots?: Record<string, unknown> | null,
): ReportProblemSubmission {
  return {
    idempotencyKey: report.idempotencyKey,
    note: report.note,
    severity: report.severity,
    screenContext: report.screenContext,
    itemContext: report.itemContext,
    env: report.env,
    screenshots: screenshots ?? null,
    ...(report.isConsentToContact ? { isConsentToContact: true as const } : {}),
  };
}

export function useReportSubmit(): ReportSubmit {
  // Guard against overlapping flushes (mount + an `online` event firing together).
  const flushing = useRef(false);

  const flush = useCallback(async (): Promise<void> => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      const queued = await listQueuedReportProblems();
      for (const report of queued) {
        try {
          // Flushed items have no screenshots — they were online-only at submit.
          await postReportProblem(toSubmission(report, null));
          await removeQueuedReportProblem(report.idempotencyKey);
        } catch (err) {
          if (isPermanentReject(err)) {
            await removeQueuedReportProblem(report.idempotencyKey);
            continue;
          }
          // Offline / 401 / transient — stop the drain; a later flush retries.
          break;
        }
      }
    } finally {
      flushing.current = false;
    }
  }, []);

  const submit = useCallback(
    async (args: SubmitReportProblemArgs): Promise<ReportSubmitOutcome> => {
      const key = randomUuid();
      const queued: QueuedReportProblem = {
        idempotencyKey: key,
        note: args.note ?? null,
        severity: args.severity ?? null,
        screenContext: args.screenContext ?? null,
        itemContext: args.itemContext ?? null,
        env: args.env ?? null,
        isConsentToContact: args.isConsentToContact,
        queuedAt: Date.now(),
      };

      // Durable-first: write to the outbox BEFORE any network attempt, so a
      // crash / close between here and the send never loses the report.
      await enqueueReportProblem(queued);

      // Offline → leave it queued; the UI confirms "we'll send it later".
      if (!isOnline()) return 'queued';

      try {
        await postReportProblem(toSubmission(queued, args.screenshots ?? null));
        await removeQueuedReportProblem(key);
        return 'sent';
      } catch {
        // Send failed — stays in the outbox; a later flush will retry.
        return 'queued';
      }
    },
    [],
  );

  // Flush on mount + on every reconnect.
  useEffect(() => {
    void flush();
    const onOnline = () => void flush();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
      return () => window.removeEventListener('online', onOnline);
    }
    return undefined;
  }, [flush]);

  return { submit, flush };
}
